import { motion } from "framer-motion";
import { Shield, Eye, Lock, Server, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SEO, breadcrumbJsonLd } from "@/lib/seo";

const Privacy = () => {
    const { t } = useTranslation();

    const sections = [
        { key: "collect" as const, icon: <Eye size={24} className="text-primary" /> },
        { key: "use" as const, icon: <Lock size={24} className="text-primary" /> },
        { key: "protect" as const, icon: <Shield size={24} className="text-primary" /> },
        { key: "cookies" as const, icon: <Server size={24} className="text-primary" /> },
    ];

    return (
        <>
            <SEO
                title={t("meta.privacy_title")}
                description={t("meta.privacy_description")}
                path="/privacy"
                jsonLd={breadcrumbJsonLd([
                    { name: t("nav.home"), path: "/" },
                    { name: t("nav.privacy"), path: "/privacy" },
                ])}
            />
            <div className="pt-28 sm:pt-32 pb-16 sm:pb-24 min-h-[100svh]">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mb-24">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-success/10 border border-success/20 text-success text-xs font-black uppercase tracking-widest mb-8"
                        >
                            <Shield size={14} />
                            {t("privacy.badge")}
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 tracking-tight"
                        >
                            {t("privacy.title_prefix")} <span className="text-muted-foreground">{t("privacy.title_highlight")}</span>
                        </motion.h1>
                        <p className="text-xl md:text-2xl text-muted-foreground font-medium leading-relaxed max-w-2xl">
                            {t("privacy.subtitle")}
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-8 max-w-6xl">
                        {sections.map((section, idx) => (
                            <motion.div
                                key={section.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 + idx * 0.1 }}
                                className="bento-card p-12 group hover:border-primary/20 transition-all"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-8 group-hover:bg-primary/20 transition-colors">
                                    {section.icon}
                                </div>
                                <h3 className="text-2xl font-black text-white mb-6 underline decoration-primary/30 underline-offset-8 decoration-2">
                                    {t(`privacy.sections.${section.key}_title`)}
                                </h3>
                                <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                                    {t(`privacy.sections.${section.key}_body`)}
                                </p>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="mt-16 p-12 rounded-[2.5rem] bg-white/[0.01] border border-white/5 max-w-4xl"
                    >
                        <h2 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
                            <FileText className="text-primary" />
                            {t("privacy.changes_title")}
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            {t("privacy.changes_body_1")}
                            <br />
                            <br />
                            {t("privacy.changes_body_2")}{" "}
                            <a href="mailto:info@trbd.net" className="text-primary hover:underline">
                                info@trbd.net
                            </a>
                        </p>
                    </motion.div>
                </div>
            </div>
        </>
    );
};

export default Privacy;
